import { Tag, TagService } from '../../tag';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { Component, OnInit, Input, EventEmitter, Output, Inject } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { AlertService } from '@app/_services';
import { OrderProductService } from '@app/_components/order-product';
import * as moment from 'moment';
import { PrimeNGConfig } from 'primeng/api';
import { first } from 'rxjs/operators';

@Component({
    selector: 'app-tag',
    templateUrl: './tag.component.html',
    styleUrls: ['./tag.component.css'],
    providers: [MessageService, ConfirmationService],
    standalone: false
})
export class TagComponent implements OnInit {

	constructor(
		private primengConfig: PrimeNGConfig,
		private tagService: TagService,
		private orderProductService: OrderProductService,
		private alertService: AlertService,
		private messageService: MessageService,
		private confirmationService: ConfirmationService,
		private formBuilder: UntypedFormBuilder,
		@Inject(MAT_DIALOG_DATA) data: any) {
		this.formTitle = data.title,
			this.tag = data.tag,
			this.visibilities = tagService.visibilities;
		this.tagTypes = tagService.tagTypes;
	}

	@Input() tag: Tag;
	@Output() doAdd = new EventEmitter<any>();
	@Output() doUpdate = new EventEmitter<any>();
	@Output() doDelete = new EventEmitter<any>();

	// data
	tagId: number;

	// forms
	form1: UntypedFormGroup;
	formLoading = false;
	formAlert: String;
	formTitle: String;

	visibilities: any;
	tagTypes: any;

	// R1 (Approach A) — order-product dropdown state.
	// Populated on dialog open with all order products. The Tag dialog's
	// "Triggered by Order Product" dropdown lets admins set/clear the FK on
	// task_tag.orderProductId, replacing the legacy folio-tag-mapping screen.
	// Only shown when tagType supports OP-triggering (currently just reservation = 8).
	orderProductOptions: { label: string; value: number }[] = [];

	/** Reactive helper for the template — shows/hides the OP-trigger field by tagType. */
	get showOrderProductField(): boolean
	{
		return this.tagService.supportsOrderProductTrigger(this.form1?.get('tagType')?.value);
	}

	// R6 — Breezeway Tag ID picker state.
	// The picker has two steps: pick a property (admin enters a Breezeway property id),
	// then pick a tag from that property's Breezeway tag list. Both values are required
	// by the dedicated PATCH /tags/{id}/breezeway-tag-id endpoint (server validates the
	// breezewayTagId against that property's list).
	breezewayPickerOpen = false;
	breezewayPropertyId: number | null = null;     // step 1
	breezewayTagsLoading = false;
	breezewayTagsError: string | null = null;
	breezewayTagOptions: any[] = [];                // step 2 — populated after lookup
	breezewayWarning: string | null = null;         // shown when admin picks an id already used by another tag

	// Sentinel value for the "Other" option — triggers the free-text input.
	static readonly OTHER_OPTION: { label: string; value: string } = { label: 'Other', value: 'Other' };

	// Dropdown options — populated dynamically from /tags/categories + "Other".
	// Initialised with just "Other" so the form is usable before the HTTP call resolves.
	tagGroupCategoryOptions: { label: string; value: string }[] = [TagComponent.OTHER_OPTION];

	// Names actually in use in the DB — used to decide whether an edited tag's
	// existing category should show up as a known option or fall into "Other".
	private knownCategoryNames = new Set<string>();

	validationMessages = {
		'tagId': [],
		'name': [
			{ type: 'required', message: 'Required' },
			{ type: 'minlength', message: '5 characters minimum' }],
		'description': [
			{ type: 'required', message: 'Required' }
		],
		'tagType': [],
		'visibility': [],
	};

	ngOnInit() {
		this.primengConfig.ripple = true;
		this.formBuild();
		// Load categories, then (if editing) patch the form with the tag.
		// Patching happens inside the subscribe callback so the dropdown options
		// are populated before we try to set a value on the preset control.
		this.loadCategoriesThenPatch();
		// R1 (Approach A) — load OPs for the "Triggered by Order Product" dropdown.
		this.loadOrderProducts();
	}

	private loadOrderProducts() {
		// Why findAllOrderProducts (and not findAll): GET /api/2.6/order-products is paginated
		// server-side (Spring's default Pageable returns ~20 rows). The /find-all variant is
		// the unpaginated endpoint that returns the entire OP catalog in one call — required
		// here because the dropdown needs all options at once for the in-component search.
		this.orderProductService.findAllOrderProducts().pipe(first()).subscribe(
			(res: any) => {
				// Response shape varies by endpoint — handle the common variants.
				const list = res?.data?.orderProducts
					|| res?.data?.orderProductList
					|| res?.data?.orderProductVOList
					|| res?.data?.list
					|| [];
				this.orderProductOptions = list
					.filter((op: any) => op && op.orderProductId != null)
					.map((op: any) => ({
						label: `${ op.name || '(unnamed)' } (id ${ op.orderProductId })`,
						value: op.orderProductId
					}))
					.sort((a: any, b: any) => a.label.localeCompare(b.label));
			},
			(err: any) => {
				console.log('loadOrderProducts FAIL: ' + JSON.stringify(err));
			}
		);
	}

	private loadCategoriesThenPatch() {
		this.tagService.getCategories()
			.pipe(first())
			.subscribe(
				(res: any) => {
					const cats: { name: string; count: number }[] = (res && res.data && res.data.categories) || [];
					this.knownCategoryNames = new Set(cats.map(c => c.name));
					this.tagGroupCategoryOptions = [
						...cats.map(c => ({ label: c.name, value: c.name })),
						TagComponent.OTHER_OPTION
					];
					this.applyEditPatchIfNeeded();
				},
				(err: any) => {
					console.log('getCategories FAIL: ' + JSON.stringify(err));
					// Even on failure, keep the form usable with just "Other" available.
					this.applyEditPatchIfNeeded();
				}
			);
	}

	private applyEditPatchIfNeeded() {
		if (!this.tag) return;
		this.tagId = this.tag.tagId;
		this.fixDates();
		const patch: any = { ...this.tag };
		const cat = this.tag.tagGroupCategory;
		if (cat && this.knownCategoryNames.has(cat)) {
			patch.tagGroupCategoryPreset = cat;
			patch.tagGroupCategoryOther = '';
		} else if (cat) {
			// Category value isn't in the current list (e.g. getCategories failed, or stale) —
			// fall back to Other so the free-text field surfaces the raw value for editing.
			patch.tagGroupCategoryPreset = 'Other';
			patch.tagGroupCategoryOther = cat;
		} else {
			patch.tagGroupCategoryPreset = null;
			patch.tagGroupCategoryOther = '';
		}
		delete patch.tagGroupCategory;
		this.form1.patchValue(patch);
	}

	isTagGroupCategoryOther(): boolean {
		return this.form1?.get('tagGroupCategoryPreset')?.value === 'Other';
	}

	private buildTagPayload(): any {
		const v = this.form1.getRawValue();
		const { tagGroupCategoryPreset, tagGroupCategoryOther, ...rest } = v;
		const preset = tagGroupCategoryPreset as string | null;
		const other = (tagGroupCategoryOther as string)?.trim() ?? '';
		const tagGroupCategory =
			preset === 'Other' ? other : (preset ?? null);
		return { ...rest, tagGroupCategory: tagGroupCategory || null };
	}

	private static tagGroupCategoryGroupValidator(group: AbstractControl): ValidationErrors | null {
		const preset = group.get('tagGroupCategoryPreset')?.value;
		const other = (group.get('tagGroupCategoryOther')?.value as string)?.trim() ?? '';
		if (preset === 'Other' && !other) {
			return { tagGroupCategoryOtherRequired: true };
		}
		return null;
	}

	onFormDelete() {
		console.log('onRowDelete: ' + JSON.stringify(event));
		this.confirmationService.confirm({
			message: 'Are you sure you want to delete Id# ' + this.tagId + '?',
			header: 'Confirm',
			icon: 'pi pi-exclamation-triangle',
			accept: () => {
				this.formDelete();
			},
			reject: () => {
				this.messageService.add({ severity: 'info', summary: 'Canceled', detail: 'Delete canceled.', life: 3000 });
			}
		});
	}

	formAdd() {
		try {
			// stop here if form is invalid
			this.form1.markAllAsTouched();
			if (this.form1.invalid) {
				return;
			}
			const payload = this.buildTagPayload();
			console.log('property-add post: ' + JSON.stringify(payload));
			this.formLoading = true;
			this.tagService.add(payload)
				.subscribe(
					(res: any) => {
						console.log('property-add SUCCESS: ' + JSON.stringify(res));
						this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message });
						this.tag = res.data.tag;
						this.doAdd.emit(this.tag);
					},
					(err: any) => {
						console.log('tag-add FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { this.formLoading = false; });

		} catch (error) {
			this.alertService.error(error);
		}
	}

	formUpdate() {
		const payload = this.buildTagPayload();
		console.log('tag: ' + JSON.stringify(payload));
		try {
			// stop here if form is invalid
			this.form1.markAllAsTouched();
			if (this.form1.invalid) {
				return;
			}
			this.formLoading = true;

			this.tagService.update(payload)
				.subscribe(
					(res: any) => {
						console.log('property-update SUCCESS: ' + JSON.stringify(res));
						this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message });
						this.tag = res.data.tag;
						this.doUpdate.emit(this.tag);
					},
					(err: any) => {
						console.log('property-update FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { this.formLoading = false; });
		} catch (error) {
			console.log('Form Invalid: ' + JSON.stringify(error));
			this.alertService.error(error);
		}
	}

	formDelete() {
		try {
			this.tagService.delete(this.tag.tagId)
				.subscribe(
					(res: any) => {
						console.log('tag-delete SUCCESS: ' + JSON.stringify(res));
						this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message });
						this.doDelete.emit(this.tag);
					},
					(err: any) => {
						console.log('tag-add FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { this.formLoading = false; });
		} catch (error) {
			this.alertService.error(error);
		}
	}

	formBuild() {
		this.form1 = this.formBuilder.group({
			tagId: [this.tagId],
			name: ['', Validators.compose(
				[
					Validators.minLength(3),
					Validators.required
				])],
			description: [],
			tagType: [4],
			visibility: [3],
			tagGroupCategoryPreset: [null],
			tagGroupCategoryOther: [''],
			// R6 — surfaced read/write so admin can manually enter or have it filled by the picker.
			// Sent by the dedicated PATCH /tags/{id}/breezeway-tag-id (NOT by the standard PATCH /tags
			// — backend ignores it on apiToVo for the standard PATCH).
			breezewayTagId: [null],
			// R1 (Approach A) — direct OP→tag link. NULL = "not auto-fired by an OP add".
			orderProductId: [null],
		}, { validators: [TagComponent.tagGroupCategoryGroupValidator] });
	}

	/* ------------------------------------------------------------------
	 * R6 — Breezeway Tag ID picker
	 * ------------------------------------------------------------------ */

	toggleBreezewayPicker() {
		this.breezewayPickerOpen = !this.breezewayPickerOpen;
		if (!this.breezewayPickerOpen) {
			this.breezewayTagOptions = [];
			this.breezewayTagsError = null;
		}
	}

	loadBreezewayTagsForProperty() {
		if (!this.breezewayPropertyId) {
			this.breezewayTagsError = 'Enter a Breezeway property id first.';
			return;
		}
		this.breezewayTagsLoading = true;
		this.breezewayTagsError = null;
		this.breezewayTagOptions = [];
		this.tagService.getBreezewayPropertyTags(this.breezewayPropertyId).pipe(first()).subscribe(
			(res: any) => {
				const list = res?.data?.breezewayTags;
				// Breezeway responses come in a few shapes — normalize to {id, name}[].
				const arr: any[] =
					Array.isArray(list) ? list :
					list?.results ?? list?.tags ?? list?.data ?? [];
				this.breezewayTagOptions = arr
					.filter((t: any) => t && t.id != null)
					.map((t: any) => ({ label: `${ t.name || '(unnamed)' } (id ${ t.id })`, value: t.id }));
				if (this.breezewayTagOptions.length === 0) {
					this.breezewayTagsError = 'No tags returned for that property.';
				}
			},
			(err: any) => {
				console.log('getBreezewayPropertyTags FAIL: ' + JSON.stringify(err));
				this.breezewayTagsError = err?.error?.message || 'Failed to load Breezeway tag list.';
				this.breezewayTagsLoading = false;
			},
			() => { this.breezewayTagsLoading = false; }
		);
	}

	/** Picker has finalized — admin chose a Breezeway tag id. Push it to the form, then save via dedicated endpoint. */
	onBreezewayTagPicked(breezewayTagId: number) {
		this.form1.get('breezewayTagId')?.setValue(breezewayTagId);
	}

	/**
	 * R6 — Persist the manually-assigned breezewayTagId via the dedicated endpoint.
	 * Requires both breezewayTagId AND a sourceBreezewayPropertyId so the backend can validate.
	 */
	saveBreezewayTagId() {
		const bwId = this.form1.get('breezewayTagId')?.value;
		if (!bwId) {
			this.messageService.add({ severity: 'warn', summary: 'Required', detail: 'Enter or pick a Breezeway tag id first.' });
			return;
		}
		if (!this.breezewayPropertyId) {
			this.messageService.add({ severity: 'warn', summary: 'Required', detail: 'Enter the Breezeway property id used for the lookup.' });
			return;
		}
		if (!this.tagId) {
			this.messageService.add({ severity: 'warn', summary: 'Save tag first', detail: 'Save the new tag once before assigning a Breezeway id.' });
			return;
		}
		this.tagService.updateBreezewayTagId(this.tagId, {
			breezewayTagId: bwId,
			sourceBreezewayPropertyId: this.breezewayPropertyId
		}).pipe(first()).subscribe(
			(res: any) => {
				const warning = res?.data?.breezewayTagAssign?.warning;
				this.breezewayWarning = warning || null;
				this.messageService.add({
					severity: warning ? 'warn' : 'success',
					summary: warning ? 'Saved with warning' : 'Saved',
					detail: warning || `Breezeway tag id ${ bwId } assigned.`,
					life: 6000
				});
				if (this.tag) this.tag.breezewayTagId = bwId;
			},
			(err: any) => {
				console.log('updateBreezewayTagId FAIL: ' + JSON.stringify(err));
				this.messageService.add({
					severity: 'error',
					summary: 'Save failed',
					detail: err?.error?.message || 'Could not assign Breezeway tag id.',
					life: 6000
				});
			}
		);
	}

	fixDates() {
		if (this.tag?.tagDate == null) {
			return;
		}
		this.tag.tagDate = moment(this.tag.tagDate).toDate();
	}
}
